import { Button, Input, Radio, Select, Upload } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Icon from '../Icon/Icon';
import Popup from '../Popup/Popup';
import Tag from '../Tag/Tag';
import { Props } from './CreateFeedback.model';

import styles from './CreateFeedback.module.scss';
import { feedbackTypes, useCreateFeedback } from './useCreateFeedback';

const CreateFeedback = ({ onClose }: Props) => {
	const { control, errors, getIconByFileType, handleSubmit, onChangeFiles, onPopupClose, state, submitFeedback } = useCreateFeedback({ onClose });
	const { email, files, isSubmitted, reportType, resources, showReportType, topics, users } = state;
	const intl = useIntl();

	const getTagColorByRole = (role: string) => {
		switch (role) {
			case 'Mentee': return 'grey';
			case 'Mentor': return 'blue';
			case 'Admin': return 'red';
		}
	}

	const getIconByResourceType = (resourceType: string) => {
		switch (resourceType) {
			case 'Audio': return <Icon name="microphone" color="white" type="solid" width="16px" />
			case 'Video': return <Icon name="video" color="white" type="solid" width="16px" />
			case 'Text': return <Icon name="file-alt" color="white" type="solid" width="16px" />
		}
	}

	return <Popup
		height="full-height"
		onClose={onClose}
		title={{ action: '', target: 'feedback' }}
		size="extra-small"
		actions={<div className={styles.buttonsWrapper}>
			<Button type="default" size="large" onClick={onPopupClose.bind(null, onClose)}><FormattedMessage id="buttons.cancel" /></Button>
			<Button type="primary" size="large" onClick={handleSubmit(submitFeedback)}><FormattedMessage id="buttons.submitFeedback" /></Button>
		</div>}
	>
		{!isSubmitted ? <div className={styles.content}>
			<div className={styles.fieldWrapper}>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`feedback.labels.type`} />
				</label>
				<Controller
					name="feedbackType"
					control={control}
					rules={{ required: true }}
					render={({ field }) => <Select className={`${styles.w100} ${errors['feedbackType'] && 'error'}`} {...field}>
						{feedbackTypes?.map((ft, index) => <Select.Option value={ft} key={index}><FormattedMessage id={`feedback.types.${ft}`} /></Select.Option>)}
					</Select>}
				/>
			</div>
			{showReportType && <><div className={styles.fieldWrapper}>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`feedback.labels.reportType`} />
				</label>
				<div className={styles.radioGroup}>
					<Controller
						name="reportType"
						control={control}
						render={({ field }) => <Radio.Group {...field} >
							<Radio value="user"><FormattedMessage id="feedback.labels.user" /></Radio>
							<Radio value="resource"><FormattedMessage id="feedback.labels.resource" /></Radio>
							<Radio value="none"><FormattedMessage id="feedback.labels.none" /></Radio>
						</Radio.Group>}
					/>
				</div>
			</div>
				{reportType === "user" && <div className={styles.fieldWrapper}>
					<label className={styles.floatingLabel}>
						<FormattedMessage id={`feedback.labels.user`} />
					</label>
					<Controller
						name="userReported"
						control={control}
						rules={{ required: true }}
						render={({ field }) =>
							<Select
								{...field}
								className={`${styles.w100} ${errors['userReported'] && 'error'}`}
								filterOption={(input, option: any) => {
									return option.children.props.children[1].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}}
								optionFilterProp="children"
								placeholder={intl.formatMessage({ id: "formPlaceholders.selectUser" })}
								showSearch>
								{users?.map((user, index) => <Select.Option className={styles.userOption} value={user.userId} key={index}>
									<div className={styles.userOption} >
										<div className={styles.avatar}>
											{user.imageId ?
												<img src={user.imageId} alt="" /> :
												<Icon
													color="white"
													height="12px"
													name="user"
													width="12px"
												/>}
										</div>
										<span>{`${user.firstName} ${user.lastName}`}</span>
										<span className={styles.role}><Tag color={getTagColorByRole(user.role)}><FormattedMessage id={`role.${user.role}`} /></Tag></span>
									</div>
								</Select.Option>)}
							</Select>}
					/>
				</div>}

				{reportType === "resource" && <div className={styles.fieldWrapper}>
					<label className={styles.floatingLabel}>
						<FormattedMessage id={`feedback.labels.resource`} />
					</label>
					<Controller
						name="resourceReported"
						control={control}
						rules={{ required: true }}
						render={({ field }) =>
							<Select
								{...field}
								className={`${styles.w100} ${errors['resourceReported'] && 'error'}`}
								filterOption={(input, option: any) => {
									return option.children.props.children[1].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}}
								optionFilterProp="children"
								placeholder={intl.formatMessage({ id: "formPlaceholders.selectResource" })}
								showSearch>
								{resources?.map((resource, index) => <Select.Option className={styles.userOption} value={resource.id} key={index}>
									<div className={styles.userOption} >
										<div className={styles.avatar}>
											{getIconByResourceType(resource._type)}
										</div>
										<span>{resource.title}</span>
									</div>
								</Select.Option>)}
							</Select>}
					/>
				</div>}

			</>}
			<div className={styles.fieldWrapper}>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`feedback.labels.topics`} />
				</label>
				<Controller
					name="topics"
					control={control}
					rules={{ required: true }}
					render={({ field }) =>
						<Select
							className={`${styles.w100} ${errors['topics'] && 'error'}`}
							{...field}
							placeholder={intl.formatMessage({ id: "formPlaceholders.selectTopic" })}>
							{topics?.map((topic, index) => <Select.Option value={topic} key={index}><FormattedMessage id={`feedback.topics.${topic}`} /></Select.Option>)}
						</Select>}
				/>
			</div>
			<div className={styles.fieldWrapper}>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`feedback.labels.shortDescription`} />
				</label>
				<Controller
					name="shortDescription"
					control={control}
					rules={{ required: true }}
					render={({ field }) =>
						<Input
							className={errors['shortDescription'] && 'error'}
							{...field}
							placeholder={intl.formatMessage({ id: "formPlaceholders.shortDescription" })} />}
				/>
			</div>
			<div className={`${styles.fieldWrapper} ${styles.uploads}`}>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`feedback.labels.fileAttachements`} />
				</label>
				<Upload
					accept=".jpg, .jpeg, .png, .mov, .pdf, .docx"
					showUploadList={{ showPreviewIcon: false }}
					fileList={files}
					onChange={onChangeFiles}
					maxCount={5}
					openFileDialogOnClick={files.length < 5}
					itemRender={(node, file, fileList: object[], actions: { remove: () => void }) =>
						<div className={styles.uploadedItem}>
							<Icon color="light" name={getIconByFileType(file?.originFileObj?.type)} type="solid" width="16px" />
							<span>{file.originFileObj?.name}</span>
							<div className={styles.remove} onClick={actions.remove}><Icon color="red" name="times" width="16px" /></div>
						</div>}
				>
					<Input value="" className={styles.uploadInput} placeholder={files.length >= 5 ? intl.formatMessage({ id: "formPlaceholders.maxFiles" }) : intl.formatMessage({ id: "formPlaceholders.upload" })} />
				</Upload>
			</div>
			<div className={styles.fieldWrapper}>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`feedback.labels.description`} />
				</label>
				<Controller
					name="description"
					control={control}
					rules={{ required: true }}
					render={({ field }) =>
						<TextArea
							className={errors['description'] && 'error'}
							{...field}
							rows={10}
							placeholder={intl.formatMessage({ id: "formPlaceholders.description" })} />}
				/>
			</div>
		</div> :
			<div className={styles.finishReview} >
				<div className={`${styles.content} ${styles.scrollable}`}>
					<span className={styles.closeIcon} onClick={onPopupClose.bind(null, onClose)}>
						<Icon name='times' color='black' />
					</span>
					<Icon className={styles.icon} color="green" name="badge-check" type="light" />
					<h1><FormattedMessage id="feedback.thankYou.title" /></h1>
					<div className={styles.info}><FormattedMessage id="feedback.thankYou.info" values={{ email }} /></div>
					<Button type="link" onClick={onPopupClose.bind(null, onClose)}><FormattedMessage id="feedback.thankYou.finish" /></Button>
				</div>
			</div>
		}
	</Popup>
}
export default CreateFeedback;